/**
 * Gallery grid
 * -----------------------------------------------------------------------------
 * Drives the "Main image + 2x2 grid" product gallery layout.
 *
 * The main image on the left shows whichever media carries the is-active class,
 * which media-gallery.js already manages. This element keeps the 2x2 grid on
 * the right showing the four media items that follow the active one, wrapping
 * around at the end of the list, and keeps the overlay counter in step.
 * Swiping left or right on the main image moves to the next or previous media.
 */
if (!customElements.get('gallery-grid')) {
  customElements.define(
    'gallery-grid',
    class GalleryGrid extends HTMLElement {
      static VISIBLE_CELLS = 4;

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.gallery = this.closest('media-gallery');
        if (!this.gallery) return;

        this.currentElement = this.querySelector('[data-gallery-current]');
        this.totalElement = this.querySelector('[data-gallery-total]');

        this.onNavClick = this.onNavClick.bind(this);
        this.onZoomClick = this.onZoomClick.bind(this);
        this.scheduleRender = this.scheduleRender.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);

        this.querySelector('[data-gallery-nav]')?.addEventListener('click', this.onNavClick);
        this.querySelector('[data-gallery-zoom]')?.addEventListener('click', this.onZoomClick);

        // media-gallery.js swaps the is-active class when a cell or a variant
        // changes the selection, so watching for it covers every entry point.
        this.observer = new MutationObserver(this.scheduleRender);
        this.observeViewer();
        this.bindSwipe();

        this.render();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.swipeTarget?.removeEventListener('touchstart', this.onTouchStart);
        this.swipeTarget?.removeEventListener('touchend', this.onTouchEnd);
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      get viewerList() {
        return this.querySelector(`#Slider-Gallery-${this.sectionId}`);
      }

      get slides() {
        return Array.from(this.querySelectorAll(`#Slider-Gallery-${this.sectionId} > [data-media-id]`));
      }

      get cells() {
        return Array.from(this.querySelectorAll(`#Slider-Thumbnails-${this.sectionId} > [data-target]`));
      }

      observeViewer() {
        const list = this.viewerList;
        if (!list) return;

        this.observer.disconnect();
        this.observer.observe(list, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true,
          childList: true,
        });
      }

      scheduleRender() {
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.render();
        });
      }

      activeIndex(slides) {
        const index = slides.findIndex((slide) => slide.classList.contains('is-active'));
        return index === -1 ? 0 : index;
      }

      render() {
        const slides = this.slides;
        const cells = this.cells;
        const total = slides.length;
        if (total === 0) return;

        const active = this.activeIndex(slides);

        // Guarantee one visible slide even if nothing carries is-active yet.
        if (!slides[active].classList.contains('is-active')) {
          slides[active].classList.add('is-active');
        }

        cells.forEach((cell, index) => {
          const offset = (index - active + total) % total;
          const visible = offset >= 1 && offset <= GalleryGrid.VISIBLE_CELLS;

          cell.toggleAttribute('hidden', !visible);
          cell.style.order = offset;
        });

        if (this.currentElement) this.currentElement.textContent = active + 1;
        if (this.totalElement) this.totalElement.textContent = total;

        this.toggleAttribute('data-single-media', total <= 1);
      }

      onNavClick(event) {
        const button = event.target.closest('button[name="previous"], button[name="next"]');
        if (!button) return;

        this.step(button.name === 'next' ? 1 : -1);
      }

      step(direction) {
        const slides = this.slides;
        const total = slides.length;
        if (total <= 1) return;

        const next = (this.activeIndex(slides) + direction + total) % total;
        this.gallery.setActiveMedia?.(slides[next].dataset.mediaId, false);
      }

      /* ---------- Swipe on the main image ---------- */

      bindSwipe() {
        this.swipeTarget = this.viewerList;
        if (!this.swipeTarget) return;

        // Keep vertical page scrolling native; horizontal moves are handled here
        this.swipeTarget.style.touchAction = 'pan-y';

        this.swipeTarget.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.swipeTarget.addEventListener('touchend', this.onTouchEnd, { passive: true });
      }

      onTouchStart(event) {
        if (event.touches.length !== 1) {
          this.touchStart = null;
          return;
        }

        // Leave 3D models and video controls alone
        if (event.target.closest('model-viewer, video, iframe, .shopify-model-viewer-ui')) {
          this.touchStart = null;
          return;
        }

        const touch = event.touches[0];
        this.touchStart = { x: touch.clientX, y: touch.clientY, time: Date.now() };
      }

      onTouchEnd(event) {
        if (!this.touchStart) return;

        const touch = event.changedTouches[0];
        const dx = touch.clientX - this.touchStart.x;
        const dy = touch.clientY - this.touchStart.y;
        const elapsed = Date.now() - this.touchStart.time;
        this.touchStart = null;

        // A swipe is a mostly horizontal move of at least 40px within 800ms
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2 || elapsed > 800) return;

        this.step(dx < 0 ? 1 : -1);
      }

      onZoomClick() {
        // Reuse the stock Dawn lightbox by triggering the active media's opener.
        this.querySelector('.gallery-grid__slide.is-active .product__media-toggle')?.click();
      }
    }
  );
}